/**
 * Conditions / problems targeted by /conditions/:slug pages.
 *
 * Each entry powers:
 *   1. /conditions/:slug - global condition page
 *   2. /conditions/:slug-in-:city - city-specific condition page
 *
 * `relatedServiceSlugs` are catalog slugs from the live service_catalog
 * (soothecaredb) - what clinics store on services[].slug - so the page can
 * filter clinics and deep-link to /:service-in-:city pages.
 */

export interface Problem {
  slug: string;
  /** H1-friendly title without "Treatment" suffix. e.g. "Back Pain" */
  name: string;
  /** Used in titles. e.g. "Back Pain Treatment & Physiotherapy" */
  fullName: string;
  /** SEO meta description starter - get city/sector appended in-page. */
  metaSnippet: string;
  /** What this is - used as opening content paragraph. */
  description: string;
  symptoms: string[];
  causes: string[];
  /** Live-catalog service slugs used to filter clinics and deep-link. */
  relatedServiceSlugs: string[];
  /** Authoritative external reading (NHS, WHO). Rendered as "Further reading" with outbound links. */
  sources?: { label: string; url: string }[];
  /** Quick FAQ cluster. Mixed with city-flavored FAQs at render time. */
  faqs: { question: string; answer: string }[];
}

export const PROBLEMS: Problem[] = [
  {
    slug: 'back-pain',
    name: 'Back Pain',
    fullName: 'Back Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for back pain treatment.',
    description:
      'Back pain is one of the most common reasons people see a physiotherapist in India. It can show up as a dull lower back ache after long hours of sitting, sharp pain when bending, or radiating pain down the leg (sciatica). Most cases are mechanical and respond very well to physiotherapy without surgery.',
    symptoms: [
      'Dull ache or stiffness in the lower back',
      'Sharp pain when bending, twisting or lifting',
      'Pain radiating down one leg (sciatica)',
      'Difficulty standing or sitting for long periods',
      'Muscle spasms or tightness',
    ],
    causes: [
      'Poor posture and long desk hours',
      'Lifting heavy objects with bad form',
      'Weak core and back muscles',
      'Slip disc / herniated disc',
      'Lumbar spondylosis (age-related wear)',
    ],
    relatedServiceSlugs: ['back-pain-therapy', 'sciatica-pain-therapy', 'slip-disc-treatment', 'lumbar-spondylosis-treatment', 'core-strengthening-program'],
    sources: [
      { label: 'NHS: Back pain', url: 'https://www.nhs.uk/conditions/back-pain/' },
      { label: 'WHO: Low back pain fact sheet', url: 'https://www.who.int/news-room/fact-sheets/detail/low-back-pain' },
    ],
    faqs: [
      {
        question: 'How many physiotherapy sessions do I need for back pain?',
        answer:
          'Most patients see meaningful improvement in 6 to 12 sessions. Mild mechanical back pain may resolve in 4-6 sessions; chronic or disc-related pain typically takes 10-15 sessions plus a home exercise plan.',
      },
      {
        question: 'Should I get an MRI before starting physiotherapy?',
        answer:
          'For most back pain, no. A physiotherapist can assess and start treatment without imaging. MRI is recommended only if you have red-flag symptoms like leg weakness, numbness, or loss of bladder/bowel control.',
      },
      {
        question: 'Is physiotherapy or chiropractic better for back pain?',
        answer:
          'Physiotherapy is more comprehensive - it combines manual therapy, exercise prescription, and posture correction. Indian medical guidelines and most insurers recognise physiotherapy as the first line of conservative care.',
      },
      {
        question: 'Can back pain be cured with physiotherapy alone?',
        answer:
          'For 80-90% of mechanical back pain cases, yes. Physiotherapy combined with daily exercises can prevent recurrence. Surgery is reserved for severe disc compression with neurological signs.',
      },
    ],
  },
  {
    slug: 'neck-pain',
    name: 'Neck Pain',
    fullName: 'Neck Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for neck pain and cervical spondylosis.',
    description:
      'Neck pain - including "tech neck" from phones and laptops - is now one of the fastest-growing reasons people seek physiotherapy in NCR. It often presents as stiffness, headaches, and sometimes numbness or tingling down the arm.',
    symptoms: [
      'Stiffness in the neck and upper shoulders',
      'Headaches that start at the base of the skull',
      'Pain when turning the head',
      'Numbness or tingling down one arm',
      'Reduced range of motion',
    ],
    causes: [
      'Forward head posture from phone / laptop use',
      'Long meetings without breaks',
      'Cervical spondylosis',
      'Stress-induced muscle tension',
      'Old whiplash injuries',
    ],
    relatedServiceSlugs: ['neck-pain-therapy', 'cervical-spondylosis-treatment', 'posture-correction-program', 'ergonomic-therapy'],
    sources: [
      { label: 'NHS: Neck pain and stiff neck', url: 'https://www.nhs.uk/conditions/neck-pain-and-stiff-neck/' },
    ],
    faqs: [
      {
        question: 'Why is my neck pain worse after work?',
        answer:
          'Most likely you\'re holding a forward-head posture for hours at the screen, which loads the cervical muscles 4-5x normal. Physiotherapy with posture correction and strengthening usually fixes this in 4-8 sessions.',
      },
      {
        question: 'Is cervical spondylosis the same as a slip disc?',
        answer:
          'No. Cervical spondylosis is age-related wear-and-tear of the neck vertebrae; slip disc is a specific herniation. Both can cause neck pain but the rehab plans differ.',
      },
      {
        question: 'Will a neck pillow fix my neck pain?',
        answer:
          'A good cervical pillow can help while sleeping but won\'t fix the underlying weakness or posture issue. Combine the pillow with physiotherapy for lasting relief.',
      },
      {
        question: 'Can physiotherapy help neck pain from long hours on a phone or laptop?',
        answer:
          'Yes. Most desk and phone related neck pain responds to a mix of mobility work, strengthening of the deep neck muscles and changes to how your screen and chair are set up. Your physiotherapist will show you which of the three matters most in your case.',
      },
    ],
  },
  {
    slug: 'sciatica',
    name: 'Sciatica',
    fullName: 'Sciatica Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for sciatica treatment without surgery.',
    description:
      'Sciatica is pain that radiates along the sciatic nerve - from the lower back, through the hip, and down the leg. It is usually caused by a slipped disc or piriformis muscle compressing the nerve. Most cases respond well to physiotherapy without surgery.',
    symptoms: [
      'Pain radiating from lower back down one leg',
      'Burning or electric-shock sensation in the leg',
      'Numbness or tingling in the foot',
      'Pain that worsens when sitting',
      'Weakness in the affected leg',
    ],
    causes: [
      'Herniated disc pressing on the nerve root',
      'Piriformis syndrome',
      'Spinal stenosis',
      'Long hours of sitting',
      'Sudden lifting injury',
    ],
    relatedServiceSlugs: ['sciatica-pain-therapy', 'slip-disc-treatment', 'back-pain-therapy', 'lumbar-spondylosis-treatment'],
    sources: [
      { label: 'NHS: Sciatica', url: 'https://www.nhs.uk/conditions/sciatica/' },
    ],
    faqs: [
      {
        question: 'How long does sciatica take to heal with physiotherapy?',
        answer:
          'Mild to moderate sciatica typically improves in 6-12 weeks of physiotherapy combined with daily exercises. Severe cases with persistent leg weakness may need 3-4 months.',
      },
      {
        question: 'Should I rest or stay active with sciatica?',
        answer:
          'Stay active. Bed rest beyond 1-2 days actually worsens recovery. Gentle walking and physiotherapist-prescribed exercises are the gold standard.',
      },
      {
        question: 'Do I need surgery for sciatica?',
        answer:
          'About 90% of sciatica patients recover with physiotherapy alone. Surgery is considered only if there is progressive weakness, loss of bladder/bowel control, or no improvement after 3 months of conservative treatment.',
      },
      {
        question: 'When is sciatica an emergency?',
        answer:
          'Seek emergency care immediately if you lose control of your bladder or bowel, feel numbness around the groin or inner thighs, or notice weakness in both legs. These can signal pressure on the spinal nerves that needs urgent medical treatment, not physiotherapy.',
      },
    ],
  },
  {
    slug: 'slip-disc',
    name: 'Slip Disc',
    fullName: 'Slip Disc Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for slip disc / herniated disc treatment.',
    description:
      'Slip disc (herniated or prolapsed disc) happens when the soft inner part of a spinal disc bulges out and presses on a nerve. Most slip discs improve dramatically with physiotherapy - surgery is needed in less than 10% of cases.',
    symptoms: [
      'Sharp pain in the back or neck',
      'Pain that radiates into a leg or arm',
      'Numbness or tingling',
      'Muscle weakness',
      'Difficulty walking or standing',
    ],
    causes: [
      'Sudden lifting with poor form',
      'Long-term poor posture',
      'Age-related disc degeneration',
      'Repetitive bending and twisting',
      'Trauma or fall',
    ],
    relatedServiceSlugs: ['slip-disc-treatment', 'back-pain-therapy', 'sciatica-pain-therapy', 'core-strengthening-program'],
    sources: [
      { label: 'NHS: Slipped disc', url: 'https://www.nhs.uk/conditions/slipped-disc/' },
    ],
    faqs: [
      {
        question: 'Can a slipped disc heal on its own with physiotherapy?',
        answer:
          'Yes, in most cases. The disc material gets reabsorbed by the body over weeks to months, and physiotherapy speeds this by reducing nerve irritation, restoring movement and strengthening the spine.',
      },
      {
        question: 'How long is slip disc rehab?',
        answer:
          'Typical physiotherapy programs run 8-16 weeks. The first 2-3 weeks focus on pain control; later weeks build strength and stability so the disc has long-term support.',
      },
      {
        question: 'Do I need to stop all activity with a slipped disc?',
        answer:
          'No. Bed rest beyond a day or two usually slows recovery. Your physiotherapist will guide you on positions and movements that ease the nerve and a gradual return to walking and daily tasks.',
      },
      {
        question: 'Is surgery the only cure for a slip disc?',
        answer:
          'Most people improve without surgery as the disc material is reabsorbed and the muscles around the spine are strengthened. Surgery is considered when nerve pain or weakness persists despite several weeks of good rehabilitation, and that decision sits with a spine specialist.',
      },
    ],
  },
  {
    slug: 'frozen-shoulder',
    name: 'Frozen Shoulder',
    fullName: 'Frozen Shoulder Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for frozen shoulder (adhesive capsulitis).',
    description:
      'Frozen shoulder (adhesive capsulitis) is a painful condition where the shoulder joint capsule thickens and tightens, severely limiting movement. It commonly affects people aged 40-60, especially women and those with diabetes.',
    symptoms: [
      'Severe pain in the shoulder',
      'Inability to lift the arm overhead',
      'Difficulty reaching back (e.g., fastening a bra)',
      'Pain at night that disturbs sleep',
      'Stiffness that progresses over months',
    ],
    causes: [
      'Diabetes (3x higher risk)',
      'Prolonged shoulder immobility after injury',
      'Age 40-60 (especially women)',
      'Thyroid disorders',
      'Idiopathic (no clear cause)',
    ],
    relatedServiceSlugs: ['frozen-shoulder-therapy', 'shoulder-pain-therapy', 'myofascial-pain-therapy'],
    sources: [
      { label: 'NHS: Frozen shoulder', url: 'https://www.nhs.uk/conditions/frozen-shoulder/' },
    ],
    faqs: [
      {
        question: 'How long does frozen shoulder take to recover?',
        answer:
          'Without treatment, frozen shoulder can take 1-3 years. With consistent physiotherapy, most patients regain near-full function in 4-9 months.',
      },
      {
        question: 'Are steroid injections better than physiotherapy?',
        answer:
          'Steroid injections give faster pain relief in the early "freezing" phase but don\'t restore range of motion. Physiotherapy is essential for long-term recovery; injections + physio is often the most effective combination.',
      },
      {
        question: 'How long does a frozen shoulder take to recover?',
        answer:
          'Left alone it can pass through painful, stiff and thawing stages over one to three years. Physiotherapy does not skip the stages but reduces pain, keeps more movement during the stiff phase and speeds the return of function.',
      },
      {
        question: 'Should I push through the pain when stretching a frozen shoulder?',
        answer:
          'No. Aggressive stretching tends to flare a frozen shoulder. Gentle movement within tolerance, done often, works better, and your physiotherapist will set the range for each stage.',
      },
    ],
  },
  {
    slug: 'knee-pain',
    name: 'Knee Pain',
    fullName: 'Knee Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for knee pain treatment.',
    description:
      'Knee pain affects millions of Indian adults - from young athletes with ligament injuries to older adults with arthritis. Physiotherapy is the first-line treatment for most knee conditions and can delay or avoid the need for surgery.',
    symptoms: [
      'Pain when walking, climbing stairs, or squatting',
      'Swelling around the kneecap',
      'Clicking or popping sensations',
      'Stiffness after rest',
      'Knee giving way / instability',
    ],
    causes: [
      'Osteoarthritis',
      'Ligament injuries (ACL, MCL)',
      'Meniscus tears',
      'Patellofemoral pain syndrome (runner\'s knee)',
      'Overuse from running or sports',
    ],
    relatedServiceSlugs: ['knee-pain-therapy', 'arthritis-management', 'acl-rehabilitation', 'meniscus-injury-rehabilitation', 'sports-injury-rehabilitation'],
    sources: [
      { label: 'NHS: Knee pain', url: 'https://www.nhs.uk/conditions/knee-pain/' },
    ],
    faqs: [
      {
        question: 'Can physiotherapy delay knee replacement surgery?',
        answer:
          'Yes. Studies show structured physiotherapy can delay knee replacement by 5-10 years in moderate osteoarthritis cases by strengthening surrounding muscles and improving joint mechanics.',
      },
      {
        question: 'Is walking good or bad for knee pain?',
        answer:
          'Walking is one of the best activities for most knee pain - it strengthens the quads and improves cartilage nutrition. The exception is acute injuries (first 1-2 weeks) where rest is needed.',
      },
      {
        question: 'Can physiotherapy help knee osteoarthritis without surgery?',
        answer:
          'Yes. Strengthening the thigh and hip muscles, managing body weight and adjusting activity are strongly supported for reducing osteoarthritis knee pain, and many people delay or avoid a knee replacement this way.',
      },
      {
        question: 'Is walking good or bad for knee pain?',
        answer:
          'For most knee conditions moderate walking helps, provided you build distance gradually and the knee settles within a day. Pain that steadily worsens with walking should be assessed before you continue.',
      },
    ],
  },
  {
    slug: 'shoulder-pain',
    name: 'Shoulder Pain',
    fullName: 'Shoulder Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for shoulder pain.',
    description:
      'Shoulder pain has many causes - rotator cuff injuries, impingement, frozen shoulder and tendinitis among them. Physiotherapy targets the specific tissue involved with manual therapy and progressive loading.',
    symptoms: [
      'Pain lifting the arm overhead',
      'Pain at night when lying on the affected side',
      'Weakness in the arm',
      'Clicking or catching sensation',
      'Limited range of motion',
    ],
    causes: [
      'Rotator cuff strain or tear',
      'Shoulder impingement',
      'Tendinitis from repetitive overhead activity',
      'Poor posture (rounded shoulders)',
      'Frozen shoulder',
    ],
    relatedServiceSlugs: ['shoulder-pain-therapy', 'rotator-cuff-rehabilitation', 'frozen-shoulder-therapy', 'tendonitis-treatment'],
    sources: [
      { label: 'NHS: Shoulder pain', url: 'https://www.nhs.uk/conditions/shoulder-pain/' },
    ],
    faqs: [
      {
        question: 'Can a torn rotator cuff heal without surgery?',
        answer:
          'Partial tears very often heal with physiotherapy alone. Full-thickness tears may need surgery, but even then physiotherapy is essential before and after to restore function.',
      },
      {
        question: 'How do I know if it is a rotator cuff problem or a frozen shoulder?',
        answer:
          'Rotator cuff pain is usually worse with overhead reaching and comes with weakness in specific directions, while a frozen shoulder is stiff in every direction, including when someone else moves your arm. A physiotherapy assessment tells the two apart in one visit.',
      },
      {
        question: 'How many sessions will shoulder pain need?',
        answer:
          'Six to twelve sessions is typical, but tendon problems often need a strengthening programme of about three months to fully settle, even if pain drops earlier.',
      },
      {
        question: 'Should I rest a painful shoulder completely?',
        answer:
          'Relative rest is better than complete rest. Avoid the movements that sharply provoke it, keep the shoulder moving within comfort, and start guided strengthening as soon as the acute pain allows.',
      },
    ],
  },
  {
    slug: 'arthritis',
    name: 'Arthritis',
    fullName: 'Arthritis Management & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for arthritis management.',
    description:
      'Arthritis - whether osteoarthritis or rheumatoid - causes joint pain, stiffness and reduced mobility. Physiotherapy is a cornerstone of arthritis management, slowing progression and keeping people active.',
    symptoms: [
      'Joint pain that worsens with activity',
      'Morning stiffness lasting 30+ minutes',
      'Swelling around joints',
      'Reduced range of motion',
      'Crunching / grating sensation',
    ],
    causes: [
      'Age-related cartilage wear (osteoarthritis)',
      'Autoimmune (rheumatoid arthritis)',
      'Previous joint injury',
      'Obesity (extra load on knees and hips)',
      'Genetics',
    ],
    relatedServiceSlugs: ['arthritis-management', 'knee-pain-therapy', 'hip-pain-therapy', 'mobility-training-program'],
    sources: [
      { label: 'NHS: Arthritis', url: 'https://www.nhs.uk/conditions/arthritis/' },
    ],
    faqs: [
      {
        question: 'Does physiotherapy help rheumatoid arthritis?',
        answer:
          'Yes, alongside medication. Physiotherapy maintains joint range, prevents deformity, and preserves daily function - it doesn\'t replace anti-rheumatic drugs but works synergistically.',
      },
      {
        question: 'Can physiotherapy slow down arthritis?',
        answer:
          'Physiotherapy cannot reverse changes already in the joint, but stronger muscles around a joint take load off it, and regular movement keeps it mobile. That is why exercise is the first line treatment for osteoarthritis in international guidelines.',
      },
      {
        question: 'Is exercise safe for arthritic joints?',
        answer:
          'Yes, when it is low impact and increased gradually. Swimming, cycling, walking and supervised strengthening are all safe for most people with arthritis.',
      },
      {
        question: 'Do heat and cold help arthritis pain?',
        answer:
          'Heat eases morning stiffness and cold helps a swollen, flared joint. Both give short term relief and work best alongside an exercise programme rather than instead of one.',
      },
    ],
  },
  {
    slug: 'tennis-elbow',
    name: 'Tennis Elbow',
    fullName: 'Tennis Elbow Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for tennis elbow (lateral epicondylitis).',
    description:
      'Tennis elbow (lateral epicondylitis) is pain on the outside of the elbow caused by overuse of the forearm muscles - common in racquet sports, but also in office workers, painters and anyone with repetitive wrist movements.',
    symptoms: [
      'Pain on the outside of the elbow',
      'Weak grip',
      'Pain when shaking hands or lifting a kettle',
      'Tenderness over the lateral epicondyle',
    ],
    causes: [
      'Repetitive wrist extension (typing, painting, racquet sports)',
      'Sudden increase in activity volume',
      'Poor technique',
      'Weak shoulder/scapular muscles forcing forearm overuse',
    ],
    relatedServiceSlugs: ['tennis-elbow-treatment', 'tendonitis-treatment'],
    sources: [
      { label: 'NHS: Tennis elbow', url: 'https://www.nhs.uk/conditions/tennis-elbow/' },
    ],
    faqs: [
      {
        question: 'How long does tennis elbow take to heal?',
        answer:
          'With targeted physiotherapy and load management, most cases improve in 6-12 weeks. Stubborn cases that have lasted over 6 months may take longer and benefit from eccentric loading protocols.',
      },
      {
        question: 'How long does tennis elbow take to heal?',
        answer:
          'Without treatment it commonly lingers for six to twelve months. With a progressive loading programme most people see clear improvement within six to twelve weeks.',
      },
      {
        question: 'Should I wear a brace for tennis elbow?',
        answer:
          'A counterforce brace just below the elbow can reduce pain during gripping tasks. It is a support during activity, not a substitute for the strengthening that fixes the tendon.',
      },
      {
        question: 'Do I need a steroid injection?',
        answer:
          'Injections can give quick short term relief, but studies show physiotherapy gives better outcomes at one year. Discuss the trade off with your doctor before choosing an injection.',
      },
    ],
  },
  {
    slug: 'plantar-fasciitis',
    name: 'Plantar Fasciitis',
    fullName: 'Plantar Fasciitis Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for plantar fasciitis (heel pain).',
    description:
      'Plantar fasciitis is a sharp pain at the bottom of the heel, worst with the first steps in the morning. It is the most common cause of heel pain in adults and responds well to physiotherapy.',
    symptoms: [
      'Sharp heel pain on first steps in the morning',
      'Pain after long periods of standing',
      'Tightness in the calf',
      'Tenderness on the bottom of the heel',
    ],
    causes: [
      'Tight calves and Achilles tendon',
      'Weak foot intrinsic muscles',
      'Sudden increase in walking or running',
      'Flat feet or high arches',
      'Poor footwear',
    ],
    relatedServiceSlugs: ['ankle-foot-pain-therapy', 'mobility-training-program'],
    sources: [
      { label: 'NHS: Plantar fasciitis', url: 'https://www.nhs.uk/conditions/plantar-fasciitis/' },
    ],
    faqs: [
      {
        question: 'Are insoles enough for plantar fasciitis?',
        answer:
          'Insoles help reduce load but don\'t fix the underlying weakness or tightness. Combine insoles with physiotherapy (calf stretching, foot strengthening) for fast, lasting relief.',
      },
      {
        question: 'What is the fastest way to relieve plantar fasciitis?',
        answer:
          'Calf and plantar fascia stretching, supportive footwear, reducing sudden increases in standing or running, and taping in the early weeks. Morning heel pain usually eases first, then pain after long standing.',
      },
      {
        question: 'Do insoles help plantar fasciitis?',
        answer:
          'Off the shelf cushioned insoles can offload the heel and help in the short term. They work best combined with calf and foot strengthening so the improvement lasts.',
      },
      {
        question: 'How long does plantar fasciitis take to get better?',
        answer:
          'Most people improve within six to twelve weeks of consistent treatment. Long standing cases can take up to a year, which is why starting early matters.',
      },
    ],
  },
  {
    slug: 'tmj-pain',
    name: 'TMJ Pain',
    fullName: 'TMJ Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for TMJ pain and jaw dysfunction.',
    description:
      'Temporomandibular joint (TMJ) pain affects the jaw, face and head. Physiotherapy targets jaw mechanics, posture and muscle tension, often resolving symptoms that dental approaches alone don\'t fix.',
    symptoms: [
      'Jaw pain when chewing or yawning',
      'Clicking or popping in the jaw',
      'Headaches around the temples',
      'Difficulty fully opening the mouth',
      'Ear pain or ringing',
    ],
    causes: [
      'Stress-related jaw clenching (bruxism)',
      'Forward head posture',
      'Whiplash or jaw trauma',
      'Misaligned bite',
    ],
    relatedServiceSlugs: ['tmj-jaw-pain-therapy', 'posture-correction-program'],
    sources: [
      { label: 'NHS: Temporomandibular disorder (TMD)', url: 'https://www.nhs.uk/conditions/temporomandibular-disorder-tmd/' },
    ],
    faqs: [
      {
        question: 'Should I see a dentist or a physiotherapist for TMJ pain?',
        answer:
          'Both can help. Physiotherapy targets the muscle and joint mechanics; dentistry handles bite issues. For most TMJ pain, starting with physiotherapy is non-invasive and very effective.',
      },
      {
        question: 'Can a physiotherapist treat jaw pain?',
        answer:
          'Yes. Physiotherapists treat jaw pain with hands on release of the jaw and neck muscles, controlled jaw exercises, posture work and advice on clenching and chewing habits.',
      },
      {
        question: 'Is jaw pain linked to neck pain?',
        answer:
          'Very often. The jaw and upper neck share muscles and nerve pathways, so a physiotherapist assesses both, and treating the neck frequently eases the jaw.',
      },
      {
        question: 'When should I see a dentist instead?',
        answer:
          'See a dentist if you have tooth pain, visible tooth wear from grinding, or a jaw that locks open or closed. Many people benefit from a dentist and physiotherapist working together.',
      },
    ],
  },
  {
    slug: 'hip-pain',
    name: 'Hip Pain',
    fullName: 'Hip Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for hip pain.',
    description:
      'Hip pain in adults is most often due to arthritis, bursitis, or hip impingement. Physiotherapy targets the gluteal muscles, hip mobility and walking mechanics.',
    symptoms: [
      'Pain in the groin or outer hip',
      'Pain that worsens with prolonged sitting or stairs',
      'Limited range when crossing the leg',
      'Limp when walking',
    ],
    causes: [
      'Hip osteoarthritis',
      'Trochanteric bursitis',
      'Femoroacetabular impingement',
      'Weak gluteal muscles',
    ],
    relatedServiceSlugs: ['hip-pain-therapy', 'arthritis-management'],
    sources: [
      { label: 'NHS: Hip pain', url: 'https://www.nhs.uk/conditions/hip-pain/' },
    ],
    faqs: [
      {
        question: 'Can hip pain be a sign of back problems?',
        answer:
          'Yes. Some hip pain actually originates in the lumbar spine (referred pain). A physiotherapist can differentiate them in the first assessment session.',
      },
      {
        question: 'Is my hip pain coming from the joint or my back?',
        answer:
          'True hip joint pain is usually felt in the groin and with putting on shoes or getting out of a car, while pain from the lower back is more often in the buttock or outer thigh. A physiotherapy assessment separates the two.',
      },
      {
        question: 'Can physiotherapy help hip osteoarthritis?',
        answer:
          'Yes. Strengthening the hip and thigh muscles and keeping the joint mobile reduces pain and helps many people delay or avoid a hip replacement.',
      },
      {
        question: 'How many sessions will hip pain need?',
        answer:
          'Most hip conditions improve over six to twelve sessions, with a home exercise programme continuing for a few months to hold the gains.',
      },
    ],
  },
  {
    slug: 'ankle-sprain',
    name: 'Ankle Sprain',
    fullName: 'Ankle Sprain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for ankle sprain rehabilitation.',
    description:
      'A rolled ankle is one of the most common injuries - and one of the most under-rehabbed. Without proper physiotherapy, ankle sprains have a 70% re-injury rate. Structured rehab restores stability and prevents recurrence.',
    symptoms: [
      'Sudden pain on the outside of the ankle',
      'Swelling within hours',
      'Bruising',
      'Difficulty bearing weight',
      'Feeling of instability when walking',
    ],
    causes: [
      'Awkward landing in sport',
      'Stepping on uneven ground',
      'Previous untreated sprains',
      'Weak peroneal muscles',
    ],
    relatedServiceSlugs: ['ankle-foot-pain-therapy', 'sports-injury-rehabilitation', 'ligament-injury-rehabilitation'],
    sources: [
      { label: 'NHS: Sprains and strains', url: 'https://www.nhs.uk/conditions/sprains-and-strains/' },
    ],
    faqs: [
      {
        question: 'Why do ankles keep getting sprained?',
        answer:
          'Untreated sprains leave the ankle weak and the proprioceptive (balance) sensors damaged, leading to chronic instability. Physiotherapy with balance training breaks this cycle.',
      },
      {
        question: 'Should I walk on a sprained ankle?',
        answer:
          'After the first two to three days, walking with the weight the ankle tolerates actually speeds recovery. If you cannot put any weight on it or the bone is tender, get an X-ray first to rule out a fracture.',
      },
      {
        question: 'How do I stop spraining the same ankle again?',
        answer:
          'Balance training on one leg and strengthening the muscles around the ankle cut re-injury risk sharply. Your physiotherapist progresses these until you can hop and change direction confidently.',
      },
      {
        question: 'How long does an ankle sprain take to heal?',
        answer:
          'A mild sprain settles in one to three weeks, a moderate one in three to six, and a severe sprain can take up to twelve weeks to return to sport.',
      },
    ],
  },
  {
    slug: 'wrist-pain',
    name: 'Wrist Pain',
    fullName: 'Wrist Pain Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for wrist pain and carpal tunnel.',
    description:
      'Wrist pain in office workers - from keyboard use, mouse-clicking, and phone scrolling - is increasingly common. Physiotherapy targets the wrist tendons, median nerve and forearm muscles.',
    symptoms: [
      'Pain or stiffness in the wrist',
      'Numbness in the thumb and first three fingers',
      'Weak grip strength',
      'Pain that worsens at night',
    ],
    causes: [
      'Repetitive typing / mouse use',
      'Carpal tunnel syndrome',
      'De Quervain\'s tenosynovitis',
      'Old wrist fracture',
    ],
    relatedServiceSlugs: ['workplace-physiotherapy', 'ergonomic-therapy', 'tendonitis-treatment'],
    sources: [
      { label: 'NHS: Hand pain', url: 'https://www.nhs.uk/conditions/hand-pain/' },
    ],
    faqs: [
      {
        question: 'Do I need wrist surgery for carpal tunnel?',
        answer:
          'Mild to moderate carpal tunnel often responds to physiotherapy with nerve gliding exercises, splinting and ergonomic correction. Surgery is for severe cases with constant numbness or muscle wasting.',
      },
      {
        question: 'How do I know if my wrist pain is carpal tunnel syndrome?',
        answer:
          'Carpal tunnel causes tingling or numbness in the thumb, index and middle fingers, often worse at night. Pain on the little finger side or with gripping points to a tendon or ligament problem instead, and a physiotherapist can test for each.',
      },
      {
        question: 'Can typing cause wrist pain?',
        answer:
          'Sustained positions and long hours can irritate the wrist tendons. Adjusting keyboard and mouse height, taking short movement breaks and gradually loading the tendons usually settles it.',
      },
      {
        question: 'Should I wear a wrist splint?',
        answer:
          'A splint worn at night helps carpal tunnel symptoms by keeping the wrist straight while you sleep. Wearing one all day has limited benefit and can weaken the wrist over time.',
      },
    ],
  },
  {
    slug: 'posture-issues',
    name: 'Posture Issues',
    fullName: 'Posture Correction & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for posture correction.',
    description:
      'Rounded shoulders, forward head posture and an arched lower back are now epidemic among Indians who work at desks. Posture correction physiotherapy strengthens the right muscles and stretches the right ones to restore alignment.',
    symptoms: [
      'Rounded shoulders',
      'Forward head ("text neck")',
      'Lower back fatigue at the end of the day',
      'Frequent headaches',
      'Tightness in the chest and upper back',
    ],
    causes: [
      'Long hours at a desk',
      'Phone scrolling',
      'Weak deep neck flexors and lower trapezius',
      'Tight pectoral muscles',
    ],
    relatedServiceSlugs: ['posture-correction-program', 'ergonomic-therapy', 'core-strengthening-program'],
    sources: [
      { label: 'NHS: Common posture mistakes and fixes', url: 'https://www.nhs.uk/live-well/exercise/common-posture-mistakes-and-fixes/' },
    ],
    faqs: [
      {
        question: 'Can adult posture really be corrected?',
        answer:
          'Yes. Posture is largely a matter of muscle strength, flexibility and habit - all changeable at any age. Most people see visible improvement in 6-8 weeks of consistent physiotherapy.',
      },
      {
        question: 'Is there a perfect posture I should hold?',
        answer:
          'No. Research shows variety and regular movement matter more than any single position. The goal of posture work is to make more positions comfortable, not to hold one rigidly.',
      },
      {
        question: 'How long does it take to correct posture?',
        answer:
          'Changes in strength and habit take six to twelve weeks of consistent exercise and workstation adjustments. Most people notice less end of day pain within the first two to three weeks.',
      },
      {
        question: 'Can poor posture cause headaches?',
        answer:
          'Yes. A forward head position loads the upper neck, and this is a common driver of tension type headaches. Neck mobility and strengthening often reduces both the neck pain and the headaches.',
      },
    ],
  },
  {
    slug: 'desk-job-pain',
    name: 'Desk Job Pain',
    fullName: 'Physiotherapy for Desk Job Pain',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for desk-job related pain.',
    description:
      'Long hours at a screen lead to neck, shoulder, back, wrist and even hip pain. Recovry\'s desk-job physiotherapy programs combine targeted treatment with workstation ergonomics and exercise prescription.',
    symptoms: [
      'Tightness in the upper trapezius',
      'Lower back stiffness after sitting',
      'Wrist or forearm pain',
      'Eye strain headaches',
      'Hip flexor tightness',
    ],
    causes: [
      '8+ hours of sitting daily',
      'Non-ergonomic workstation',
      'Lack of movement breaks',
      'Forward head and rounded shoulder posture',
    ],
    relatedServiceSlugs: ['workplace-physiotherapy', 'ergonomic-therapy', 'posture-correction-program', 'neck-pain-therapy', 'back-pain-therapy'],
    sources: [
      { label: 'NHS: Common posture mistakes and fixes', url: 'https://www.nhs.uk/live-well/exercise/common-posture-mistakes-and-fixes/' },
    ],
    faqs: [
      {
        question: 'How often should I take breaks at a desk?',
        answer:
          'Stand up and move every 30-60 minutes for at least 1-2 minutes. The 20-20-20 rule for eyes (every 20 min, look 20 ft away for 20 sec) also helps prevent eye-strain headaches.',
      },
      {
        question: 'How often should I take breaks from my desk?',
        answer:
          'Every thirty to forty five minutes, stand and move for one to two minutes. Short frequent breaks do more for back and neck pain than one long break.',
      },
      {
        question: 'Is a standing desk better for back pain?',
        answer:
          'Alternating between sitting and standing helps most people. Standing all day brings its own foot, knee and back complaints, so the switch is what matters.',
      },
      {
        question: 'What is a good basic desk setup?',
        answer:
          'Screen top at eye level, elbows at roughly ninety degrees with shoulders relaxed, feet flat on the floor or a footrest, and the keyboard close enough that you are not reaching.',
      },
    ],
  },
  {
    slug: 'pregnancy-pain',
    name: 'Pregnancy Pain',
    fullName: 'Physiotherapy for Pregnancy Pain',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for prenatal pregnancy pain.',
    description:
      'Pregnancy puts extra load on the lower back, pelvis and hips. Prenatal physiotherapy provides safe pain relief, exercise prescription and pelvic floor preparation for delivery.',
    symptoms: [
      'Lower back pain in 2nd or 3rd trimester',
      'Pelvic girdle pain',
      'Sciatica',
      'Wrist pain (carpal tunnel)',
      'Round ligament pain',
    ],
    causes: [
      'Hormonal changes loosening ligaments',
      'Postural changes from growing uterus',
      'Increased body weight',
    ],
    relatedServiceSlugs: ['back-pain-therapy', 'hip-pain-therapy', 'sciatica-pain-therapy'],
    sources: [
      { label: 'NHS: Back pain in pregnancy', url: 'https://www.nhs.uk/pregnancy/related-conditions/common-symptoms/back-pain/' },
    ],
    faqs: [
      {
        question: 'Is physiotherapy safe during pregnancy?',
        answer:
          'Yes, when delivered by a physiotherapist trained in prenatal care. They avoid contraindicated techniques (e.g., certain electrotherapy modalities) and use pregnancy-safe positioning.',
      },
      {
        question: 'Is physiotherapy safe during pregnancy?',
        answer:
          'Yes, with a physiotherapist trained in women\'s health. Treatment is adapted to each trimester, and lying flat on the back is avoided in later pregnancy.',
      },
      {
        question: 'What is pelvic girdle pain and can it be treated?',
        answer:
          'It is pain at the front or back of the pelvis that is common in pregnancy. A support belt, targeted exercises and advice on how to turn in bed, sit and climb stairs reduce it considerably.',
      },
      {
        question: 'When should I seek urgent care instead of physiotherapy?',
        answer:
          'Contact your obstetrician immediately for bleeding, fluid loss, reduced baby movements, severe or sudden abdominal pain, or a headache with vision changes.',
      },
    ],
  },
  {
    slug: 'postpartum-pain',
    name: 'Postpartum Pain',
    fullName: 'Physiotherapy for Postpartum Recovery',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for postpartum recovery.',
    description:
      'After delivery, mothers commonly face back pain, pelvic floor weakness, diastasis recti and wrist pain. Postnatal physiotherapy supports a safe return to activity.',
    symptoms: [
      'Lower back pain',
      'Urinary incontinence when sneezing or jumping',
      'Diastasis recti (abdominal separation)',
      'Wrist pain from feeding posture',
      'Pelvic heaviness',
    ],
    causes: [
      'Weakened pelvic floor and core',
      'Postural strain from feeding and lifting baby',
      'Hormonal effects on ligaments',
    ],
    relatedServiceSlugs: ['core-strengthening-program', 'back-pain-therapy', 'posture-correction-program'],
    sources: [
      { label: 'NHS: Your post-pregnancy body', url: 'https://www.nhs.uk/conditions/baby/support-and-services/your-post-pregnancy-body/' },
    ],
    faqs: [
      {
        question: 'How soon after delivery can I start physiotherapy?',
        answer:
          'Gentle pelvic floor work can start within days of normal delivery. Core and full-body programs typically begin 6 weeks postpartum (12 weeks after C-section) once cleared by your obstetrician.',
      },
      {
        question: 'When can I start physiotherapy after giving birth?',
        answer:
          'Gentle breathing and pelvic floor activation can begin within days. A full assessment is usually done around six weeks, or earlier after a caesarean for scar and mobility guidance once your doctor agrees.',
      },
      {
        question: 'Can physiotherapy help a separated stomach muscle after pregnancy?',
        answer:
          'Yes. Graded core exercises help most cases of diastasis recti. Heavy lifting and intense abdominal work are held back until the muscle wall has recovered.',
      },
      {
        question: 'Is leaking urine after childbirth normal?',
        answer:
          'It is common but not something to accept as permanent. Pelvic floor physiotherapy resolves or greatly improves it for most women within a few months.',
      },
    ],
  },
  {
    slug: 'balance-problems',
    name: 'Balance Problems',
    fullName: 'Balance Physiotherapy & Vestibular Rehabilitation',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for balance disorders and vestibular rehab.',
    description:
      'Dizziness, unsteadiness or recurrent falls - especially in older adults or after head injury - often respond very well to vestibular rehabilitation and balance training.',
    symptoms: [
      'Dizziness or vertigo',
      'Unsteady walking',
      'Recent falls or near-falls',
      'Difficulty walking in the dark',
      'Nausea with head movements',
    ],
    causes: [
      'Inner ear (vestibular) disorders like BPPV',
      'Age-related decline in balance systems',
      'Stroke or neurological conditions',
      'Medication side effects',
    ],
    relatedServiceSlugs: ['balance-disorder-therapy', 'vestibular-rehabilitation', 'fall-prevention-program', 'gait-training-therapy'],
    sources: [
      { label: 'NHS: Dizziness', url: 'https://www.nhs.uk/conditions/dizziness/' },
      { label: 'WHO: Falls fact sheet', url: 'https://www.who.int/news-room/fact-sheets/detail/falls' },
    ],
    faqs: [
      {
        question: 'Can BPPV be cured with physiotherapy?',
        answer:
          'Yes. Benign paroxysmal positional vertigo responds dramatically to specific repositioning maneuvers (Epley, Semont) performed by a vestibular-trained physiotherapist - often in just 1-2 sessions.',
      },
      {
        question: 'What causes balance problems?',
        answer:
          'The inner ear, muscle weakness, nerve problems in the feet, some medications and vision changes can all be involved. A physiotherapy assessment works out which factors apply to you before treatment starts.',
      },
      {
        question: 'What is vestibular rehabilitation?',
        answer:
          'A set of exercises that retrain the brain to process signals from the inner ear. It is highly effective for positional vertigo and for dizziness that lingers after an inner ear infection.',
      },
      {
        question: 'When is dizziness an emergency?',
        answer:
          'Sudden dizziness together with weakness, slurred speech, double vision, a severe headache or trouble walking needs emergency care immediately, as these can be signs of a stroke.',
      },
    ],
  },
  {
    slug: 'headache',
    name: 'Headache',
    fullName: 'Cervicogenic Headache Treatment & Physiotherapy',
    metaSnippet:
      'Find Recovry-verified physiotherapy centres for cervicogenic and tension headaches.',
    description:
      'Many recurring headaches are actually "cervicogenic" - originating in the neck. Physiotherapy targeting the upper cervical spine and trigger points often resolves headaches that don\'t respond to painkillers.',
    symptoms: [
      'Headache starting at the base of the skull',
      'One-sided pain that doesn\'t switch sides',
      'Pain triggered by neck movements',
      'Pain referred to the eye or temple',
    ],
    causes: [
      'Upper cervical spine dysfunction',
      'Forward head posture',
      'Trigger points in upper trapezius/suboccipitals',
      'Stress and clenching',
    ],
    relatedServiceSlugs: ['neck-pain-therapy', 'cervical-spondylosis-treatment', 'posture-correction-program', 'myofascial-pain-therapy'],
    sources: [
      { label: 'NHS: Headaches', url: 'https://www.nhs.uk/conditions/headaches/' },
    ],
    faqs: [
      {
        question: 'How do I know if my headache is from my neck?',
        answer:
          'Cervicogenic headaches typically start at the back of the head, are one-sided, and are reproduced or worsened by neck movement or pressure on the upper neck. A physiotherapist can confirm this in the first assessment.',
      },
      {
        question: 'Can physiotherapy help headaches?',
        answer:
          'Yes for tension type headaches and for headaches that start in the neck. Migraines are managed medically, but physiotherapy can reduce the neck and posture triggers that set them off.',
      },
      {
        question: 'How do I know if my headache comes from my neck?',
        answer:
          'Neck related headaches are usually on one side, start at the base of the skull and are brought on by neck positions or movement. A physiotherapist can reproduce and ease the headache during assessment.',
      },
      {
        question: 'Which headaches need urgent medical attention?',
        answer:
          'A sudden severe headache unlike any before, a headache with fever and a stiff neck, one that follows a head injury, or one with weakness, confusion or vision loss needs emergency care.',
      },
    ],
  },
];

export const PROBLEM_SLUGS = PROBLEMS.map((p) => p.slug);

export function findProblem(slug: string | undefined): Problem | undefined {
  return PROBLEMS.find((p) => p.slug === slug);
}
